import styled, { keyframes, css } from 'styled-components';
import { readableColor } from 'polished';

import {
  screenMdMax,
  screenMdMin,
  screenLgMin,
  spacer24,
  spacer8,
  white,
} from '@spotify-internal/encore-web';

import Button from '../../atoms/Button';
import Header from '../../atoms/Header';
import { responsiveSize } from '../../utils/helpers';

import type { HighlightColor } from './benefits-comparison';

const ShowCheckmarks = keyframes`
0% {
    opacity: 0;
    transform: scale(0.8);
  }

  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

export const ComponentWrapper = styled.div`
  --tooltip-content-font-size: ${responsiveSize(14, 16, 320, 1440)};
  -webkit-font-smoothing: antialiased;
  width: 100%;
  max-width: 686px;
  margin: 0 auto;
  text-align: center;

  > div {
    margin-top: 20px;

    @media (max-width: ${screenMdMax}) {
      width: 100%;
      max-width: 450px;
      padding-left: 25px;
      padding-right: 25px;
      margin-left: auto;
      margin-right: auto;
      box-sizing: border-box;
      a {
        width: 100%;
      }
    }
  }
`;

export const StyledHeader = styled(Header)`
  margin-left: 23px;
  margin-right: 23px;
  margin-bottom: ${spacer24};

  h2 {
    font-weight: 700;
  }
  h3 {
    -webkit-font-smoothing: antialiased;
    font-weight: 400;
    margin-top: ${spacer8};
  }

  @media screen and (min-width: ${screenMdMin}) {
    margin-left: 0;
    margin-right: 0;

    text-align: center;
  }
`;

const GridItem = styled.td<{
  highlight?: boolean;
}>`
  position: relative;
  text-align: center;
  padding: 16px 10px;
  background-color: var(--background-base);

  background-color: ${({ highlight }) =>
    highlight ? 'transparent' : 'var(--background-base)'};
`;

export const TableWrapper = styled.table<{
  highlightColor?: HighlightColor;
}>`
  table-layout: fixed;
  border-spacing: 0;
  width: 100%;
  max-width: 446px;
  margin: 0 auto;
  overflow-wrap: break-word;

  > thead {
    background: var(--background-base);
  }

  ${({ highlightColor }) => {
    if (!highlightColor) return '';
    const color =
      highlightColor.type === 'solid'
        ? highlightColor.value
        : `linear-gradient(to bottom, ${highlightColor.value.from}, ${highlightColor.value.to})`;
    return `
      background: ${color};
      --readable-color: ${
        highlightColor?.type === 'solid'
          ? readableColor(color, '#000', '#fff')
          : '#fff'
      };
    `;
  }};
`;

export const BenefitsRow = styled.tr<{
  inView?: boolean;
  benefitIndex: number;
}>`
  &:hover,
  &:focus,
  &:focus-within,
  &:focus-visible {
    > td:last-child {
      background: var(--background-tinted-highlight);
    }
    > th {
      background: var(--background-highlight);

      > div {
        background: none;
      }
    }
    td:nth-child(2) {
      background: var(--background-highlight);

      > div {
        background: none;
      }
    }
  }
  ${({ benefitIndex }) =>
    benefitIndex &&
    css`
      --animationDelay: ${benefitIndex * 100}ms;
    `}

  svg {
    ${({ inView }) =>
      inView &&
      css`
        opacity: 0;
        animation: ${ShowCheckmarks} 300ms cubic-bezier(0.75, 0.32, 0.49, 1.32)
          calc(var(--animationDelay) + 600ms) forwards;
      `}
    @media screen and (prefers-reduced-motion: reduce) {
      opacity: 1;
    }
  }
`;

// @ts-ignore
export const Benefit = styled(GridItem)`
  &[id='benefit-0'] {
    padding-bottom: 10px;
  }
  background-color: var(--background-base);

  text-align: left;
  border-bottom: 1px solid #d9dadc;
  font-weight: 400;

  &[scope='col'] {
    vertical-align: bottom;
    font-size: var(--subheadline-font-size);
    line-height: var(--subheadline-line-height);
    font-weight: 700;
  }
`;
// @ts-ignore
export const BenefitStatus = styled(GridItem)<{
  highlight?: boolean;
  useBottomBorders?: boolean;
}>`
  ${({ useBottomBorders }) =>
    !!useBottomBorders && `border-bottom: 1px solid #d9dadc;`}
  color: ${({ highlight }) =>
    highlight ? 'var(--readable-color, #FFF)' : '#FFF'};
  align-items: center;
  justify-content: center;
`;

export const BenefitStatusText = styled.span`
  &::first-letter {
    text-transform: capitalize;
  }
`;

export const BenefitContent = styled.div`
  background: var(--background-base);
  position: relative;

  &::first-letter {
    text-transform: capitalize;
  }
`;
// @ts-ignore
export const Plan = styled(GridItem)<{
  highlight?: boolean;
  $planColor?: string;
  useBottomBorders?: boolean;
  as?: string;
  index: number;
}>`
  padding: 22px 10px 22px 18px;
  &:nth-child(2) {
    border-right: 2px solid var(--background-base, #121212);
  }
  ${({ index }) => index && `animation-delay: ${index + 200}ms`}
  border-bottom: 1px solid ${white};
  ${({ useBottomBorders, $planColor }) =>
    !!useBottomBorders &&
    !!$planColor &&
    !['#fff', '#FFF'].includes($planColor) &&
    `border-bottom: 1px solid #d9dadc;`}
  text-align: left;

  ${({ $planColor }) =>
    $planColor &&
    `
    --plan-color: ${$planColor};
  `};

  height: 100%;
  width: 29%;
  box-sizing: border-box;
  background-color: var(--plan-color, --decorative-base);
  color: var(--inverted-text-base);

  ${({ highlight }) =>
    highlight &&
    `
    color: var(--readable-color, #FFF);
  `};
`;

export const PlanInfo = styled.div`
  position: relative;
  z-index: 1;
`;

export const PlanName = styled.div`
  font-weight: 700;
  font-size: var(--subheadline-font-size);
  line-height: 22px;
`;

export const BenefitsBody = styled.tbody`
  position: relative;
`;
// @ts-ignore
export const StyledButton = styled(Button)`
  text-transform: capitalize;
  font-size: var(--font-size);
  line-height: var(--line-height);
  @media (min-width: ${screenLgMin}) {
    display: none;
  }
`;
